// Guard brains — pluggable tactics for the whole escort.
//
// The built-in guard AI (Guard._builtinTarget) has every guard decide on
// its own. A brain replaces that decision for the squad as a whole, so
// guards can coordinate: surround, strike together, bait, hold formation.
// Mechanics stay fixed — a brain only says *where to walk* and *whether to
// strike now*; speed, ranges, cooldowns, damage and the lancer charge band
// are still enforced by Guard. Built for the evolution experiments
// (~/dev/corovans-ai), where brains are parameterised and evolved.
//
// Contract:
//   brain.plan(view)          optional, once per tick — squad-level decisions
//   brain.order(guard, view)  per alive guard → order | null
//
//   order = { moveTo: Vec2, attack?: bool, charge?: bool, chargeAim?: Vec2, bombAim?: Vec2,
//             arrowAim?: Vec2 }
//     moveTo  point to walk toward (null/missing = stand still)
//     attack  false holds a swing/shot that is otherwise ready (default true)
//     charge  false vetoes a lancer charge (default true)
//     chargeAim  during a lancer's windup, the point it tracks instead of the
//             player (the lunge locks onto it); speed / damage unchanged
//     bombAim  a bombardier's aim point instead of its built-in lead (the
//             ±14 px scatter still applies)
//     arrowAim  an archer's aim point instead of its built-in lead (the
//             ±6° spread and the arrow variant roll stay the same)
//   null hands that guard back to the built-in state machine, so a brain
//   can take over only some archetypes.
//
// `view` is read-only: live entity references, never mutate them (the only
// write a brain makes is the returned order).
//   { dt, wave, rng, guards, caravans, player: { pos, vel, hp, maxHp,
//     radius, attackRange, attackReady, attacking, dashing, dashReady,
//     invulnerable, stuck, damage, attackCooldown, armorBreak, damageResist,
//     regenPerSec, lifestealPct } }
//   damage / attackCooldown are effective (crit and buffs folded in) — what
//   a fight estimate needs; a human sees the same through the cards.

import { Vec2, CONST } from './utils.js';
import { GuardState } from './caravan.js';
import { calcDamage } from './combat.js';

export function guardBrainView(player, guards, caravans, { dt, wave, rng = null }) {
  return {
    dt,
    wave,
    rng,
    guards: guards.filter(g => g.alive),
    caravans: caravans.filter(c => c.alive),
    player: {
      pos: player.pos,
      vel: player.vel,
      hp: player.hp,
      maxHp: player.maxHp,
      radius: player.radius,
      attackRange: player.attackRange,
      attackReady: player.attackTimer <= 0,
      attacking: player.isAttacking,
      dashing: player.dashTimer > 0,
      dashReady: player.dashCooldownTimer <= 0,
      stuck: (player.stickyTimer || 0) > 0,
      invulnerable: player.iframeTimer > 0,
      damage: player.damage * (player.damageMul || 1) * (1 + (player.critChance || 0)),
      attackCooldown: player.attackCooldown * (player.attackCdMul || 1),
      armorBreak: !!player.armorBreak,
      damageResist: player.damageResist || 0,
      regenPerSec: player.regenPerSec || 0,
      lifestealPct: player.lifestealPct || 0,
    },
  };
}

// Run one brain tick: squad plan, then an order per alive guard. Called by
// the game loop and the simulator right before guard.update().
export function runGuardBrain(brain, view) {
  if (brain.plan) brain.plan(view);
  for (const guard of view.guards) {
    guard.order = brain.order(guard, view);
  }
}

// Point on the player's body edge facing `from` — where a melee guard
// stands to swing without walking into the player.
export function towardSurface(from, player) {
  const off = from.sub(player.pos);
  const d = off.len();
  return d > player.radius ? player.pos.add(off.mul(player.radius / d)) : from;
}

// --- Reference brain: surround, then strike together -------------------
//
// Melee guards spread onto a ring around the player, each on its own angle
// slot, and close in all at once when the ring is formed (or after a
// timeout, so a fleeing player can't stall them forever). Ranged guards,
// and escorts that haven't spotted the player yet (PATROL), keep the
// built-in behaviour — detection stays the built-in one. Doubles as a template for evolved brains:
// every tunable lives in `params`.
export const SURROUND_DEFAULTS = {
  ringRadius: 70,      // px from the player while gathering
  formedTolerance: 20, // px — a guard counts as "in slot" within this
  gatherTimeout: 2.5,  // s — strike anyway after this long
  strikeDuration: 1.5, // s — then fall back to the ring and regroup
};

export class SurroundBrain {
  constructor(params = {}) {
    this.params = { ...SURROUND_DEFAULTS, ...params };
    this._phaseTime = 0;
    this._striking = false;
    this._slots = new Map();
  }

  plan(view) {
    const { ringRadius, formedTolerance, gatherTimeout } = this.params;
    const p = view.player.pos;
    const melee = view.guards.filter(g => !g.def.ranged && g.state !== GuardState.PATROL);

    // Slots by current bearing, so guards don't cross paths to reach them.
    const bearing = g => Math.atan2(g.pos.y - p.y, g.pos.x - p.x);
    melee.sort((a, b) => bearing(a) - bearing(b));
    const base = melee.length ? bearing(melee[0]) : 0;
    this._slots.clear();
    melee.forEach((g, i) => {
      const a = base + (i / melee.length) * Math.PI * 2;
      this._slots.set(g, new Vec2(p.x + Math.cos(a) * ringRadius, p.y + Math.sin(a) * ringRadius));
    });

    this._phaseTime += view.dt;
    if (this._striking) {
      if (this._phaseTime > this.params.strikeDuration) {
        this._striking = false;
        this._phaseTime = 0;
      }
      return;
    }
    const formed = melee.every(g => g.pos.dist(this._slots.get(g)) < formedTolerance);
    if (melee.length && (formed || this._phaseTime > gatherTimeout)) {
      this._striking = true;
      this._phaseTime = 0;
    }
  }

  order(guard, view) {
    const slot = this._slots.get(guard);
    if (!slot) return null;
    if (this._striking) return { moveTo: towardSurface(guard.pos, view.player) };
    return { moveTo: slot, attack: false, charge: false };
  }
}

// --- Relentless: chase forever, no coordination ------------------------
//
// The built-in AI's only real weakness against a faster player is its
// leash (CHASE → RETURN once the player is far). This brain just never
// lets go — the baseline any coordinated brain has to beat.
export class RelentlessBrain {
  order(guard, view) {
    if (guard.def.ranged || guard.state === GuardState.PATROL) return null;
    return { moveTo: towardSurface(guard.pos, view.player) };
  }
}

// --- Pack: intercept, pincer, wait for the pack, anchor, leash -----------
//
// The player outruns every guard (95 px/s vs 80 / 55), so a ring around
// his current position never closes — pursuit turns into a conga line.
// Each rule below is a different answer to that; each has a parameter
// that switches it off, so evolution decides which ones pay:
//
//   useLeash, leash  when on, return to the caravan beyond `leash` px from it
//   anchorFrac  share of each escort that holds between caravan and player
//   packSlack   a guard this far ahead of the pack's median distance waits
//   flankWidth  pincer: halves approach offset left / right of the player
//   lead        intercept: aim at p + v · lead · (time to reach)
//
// PACK_OFF reproduces RelentlessBrain (every rule disabled).
export const PACK_DEFAULTS = {
  lead: 0.5,           // × time-to-reach, capped at PACK_MAX_LEAD_SEC
  flankWidth: 80,      // px sideways at range
  flankCloseDist: 80,  // px — inside this, drop the flank and go straight in
  packSlack: 120,      // px ahead of the median before waiting
  anchorFrac: 0,       // 0..1 of each caravan's engaged melee
  anchorRadius: 60,    // px from the caravan, on the player's side
  useLeash: false,
  leash: 1500,         // px from the caravan
};
export const PACK_OFF = {
  lead: 0, flankWidth: 0, flankCloseDist: 80, packSlack: 1e9, anchorFrac: 0, anchorRadius: 60, useLeash: false, leash: 1500,
};
const PACK_MAX_LEAD_SEC = 3;

export class PackBrain {
  constructor(params = {}) {
    this.params = { ...PACK_DEFAULTS, ...params };
    this._orders = new Map();
  }

  plan(view) {
    const P = this.params;
    const p = view.player.pos;
    const v = view.player.vel;
    this._orders.clear();
    const melee = view.guards.filter(g => !g.def.ranged && g.state !== GuardState.PATROL);
    if (melee.length === 0) return;

    // Anchors: per caravan, the engaged melee nearest to it hold the line.
    const anchors = new Set();
    if (P.anchorFrac > 0) {
      const byCaravan = new Map();
      for (const g of melee) {
        if (!g.caravan.alive) continue;
        if (!byCaravan.has(g.caravan)) byCaravan.set(g.caravan, []);
        byCaravan.get(g.caravan).push(g);
      }
      for (const [c, gs] of byCaravan) {
        gs.sort((a, b) => a.pos.dist(c.pos) - b.pos.dist(c.pos));
        gs.slice(0, Math.round(P.anchorFrac * gs.length)).forEach(g => anchors.add(g));
      }
    }

    // Pack geometry: centroid, the axis pack → player, and its normal.
    let cx = 0;
    let cy = 0;
    for (const g of melee) { cx += g.pos.x; cy += g.pos.y; }
    const centroid = new Vec2(cx / melee.length, cy / melee.length);
    const axis = p.sub(centroid);
    const axisDir = axis.lenSq() > 1 ? axis.normalize() : new Vec2(1, 0);
    const normal = new Vec2(-axisDir.y, axisDir.x);
    const dists = melee.map(g => g.pos.dist(p)).sort((a, b) => a - b);
    const median = dists[Math.floor(dists.length / 2)];
    // Pincer sides: split at the median lateral position, so each half
    // stays on the side it already is on.
    const lateral = g => g.pos.sub(p).dot(normal);
    const lats = melee.map(lateral).sort((a, b) => a - b);
    const latMedian = lats[Math.floor(lats.length / 2)];

    for (const g of melee) {
      const d = g.pos.dist(p);
      const c = g.caravan.alive ? g.caravan : null;

      if (P.useLeash && c && g.pos.dist(c.pos) > P.leash) {
        this._orders.set(g, { moveTo: c.pos });
        continue;
      }
      if (anchors.has(g)) {
        const toPlayer = p.sub(c.pos);
        const reach = P.anchorRadius + 40;
        const post = toPlayer.len() < reach
          ? towardSurface(g.pos, view.player)
          : c.pos.add(toPlayer.normalize().mul(P.anchorRadius));
        this._orders.set(g, { moveTo: post });
        continue;
      }
      if (d > P.flankCloseDist && d < median - P.packSlack) {
        this._orders.set(g, { moveTo: g.pos });
        continue;
      }
      if (d <= P.flankCloseDist) {
        this._orders.set(g, { moveTo: towardSurface(g.pos, view.player) });
        continue;
      }
      const t = Math.min(PACK_MAX_LEAD_SEC, P.lead * d / g.speed);
      const side = lateral(g) < latMedian ? -1 : 1;
      const fade = Math.min(1, (d - P.flankCloseDist) / Math.max(1, P.flankCloseDist));
      const target = p.add(v.mul(t)).add(normal.mul(side * P.flankWidth * fade));
      this._orders.set(g, { moveTo: target });
    }
  }

  order(guard) {
    return this._orders.get(guard) || null;
  }
}

// --- Fight estimate: can this group kill the player? -------------------
//
// Lanchester-style: the player cuts the group down one guard at a time,
// quickest kill first (worst case for the guards); every guard deals its
// DPS for as long as it lives. Returns guard damage / player effective HP:
// ≥ 1 means "on paper the group wins with every guard swinging non-stop".
// Real uptime is lower (dashes, retreats), which is what a threshold above
// 1 is for.
export function fightRatio(guards, player) {
  if (guards.length === 0) return 0;
  const hit = g => Math.max(1, calcDamage(player.damage, player.armorBreak ? 0 : g.armor));
  const killTime = guards
    .map(g => ({ t: Math.ceil(g.hp / hit(g)) * player.attackCooldown, dps: g.damage / g.attackCooldown, hp: g.hp }))
    .sort((a, b) => a.t - b.t);
  let elapsed = 0;
  let dealt = 0;
  let guardHp = 0;
  for (const k of killTime) {
    elapsed += k.t;
    dealt += k.dps * elapsed;
    guardHp += k.hp;
  }
  const ehp = player.hp / (1 - Math.min(0.9, player.damageResist))
    + player.regenPerSec * elapsed
    + player.lifestealPct * guardHp;
  return dealt / ehp;
}

// Single-link groups: guards closer than `linkDist` (chained) share a group.
export function linkGroups(guards, linkDist) {
  const groups = [];
  const seen = new Set();
  for (const g of guards) {
    if (seen.has(g)) continue;
    const group = [g];
    seen.add(g);
    for (let i = 0; i < group.length; i++) {
      for (const o of guards) {
        if (!seen.has(o) && o.pos.dist(group[i].pos) < linkDist) {
          seen.add(o);
          group.push(o);
        }
      }
    }
    groups.push(group);
  }
  return groups;
}

// --- Skirmish: hold as a group, then hit-and-run on the swing timer -----
//
// Idea from playtesting: stay together until the player comes close, then
// fight in rhythm with the cooldown — close in when the swing is ready,
// back off and spread out while it recharges, so the player's wide melee
// arc catches one guard instead of three.
//
//   holdDist    player farther than this from the group → hold together
//   groupRadius how tight the group holds (px from its centroid)
//   readyLead   s before the swing is ready to start closing in
//   retreatDist px from the player to back off to while recharging (0 = stay)
//   spread      px kept from the nearest fellow guard (0 = ignore)
//   waitSwing   true (or > 0.5): a ready guard closes in only right after the player
//               swung (his attack on cooldown) or when already in reach
//   bossRally   true (or > 0.5): on a boss wave every guard (any caravan, any state)
//               rallies to the boss caravan and the group holds there —
//               the regular caravans are given up to protect the big loot
//   massThreshold  critical mass: a group fights only if fightRatio ≥ this,
//               otherwise its melee fall back to the nearest other group
//               (0 = off). If even all guards together fall short, fight.
//   linkDist    px — guards closer than this form one group
//
// Borrowed from PackBrain / SurroundBrain (all off by default), to test
// whether they add anything on top of the hit-and-run core:
//   lead, flankWidth, flankCloseDist  how a ready guard closes in: intercept
//               (aim at p + v · lead · time-to-reach) and pincer (halves offset
//               left / right), dropped inside flankCloseDist
//   ringSlots   true: ready guards close in on evenly spaced slots around the
//               player (by bearing) instead of the nearest point
//   anchorFrac, anchorRadius  share of each escort holding between its caravan
//               and the player until he comes within anchorRadius + 40
//   useLeash, leash  when on, a guard farther than `leash` px from its
//               caravan walks back (a flag, so "off" isn't a dead zone of
//               leash values larger than the world)
//
// SKIRMISH_OFF reproduces RelentlessBrain.
export const SKIRMISH_DEFAULTS = {
  holdDist: 220,
  groupRadius: 40,
  readyLead: 0.2,
  retreatDist: 70,
  spread: 30,
  waitSwing: 0,
  bossRally: 0,
  massThreshold: 0,
  linkDist: 120,
  lead: 0,
  flankWidth: 0,
  flankCloseDist: 80,
  ringSlots: 0,
  anchorFrac: 0,
  anchorRadius: 60,
  useLeash: false,
  leash: 1500,
  // Lancer tactics (SkirmishBrain._lancerOrder) — off by default.
  chargeWhenStuck: false,
  chargeWhenNoDash: false,
  chargeWhenEngaged: 0,
  engagedRadius: 60,
  chargeWhenSwinging: false,
  chargeMinD: 0,
  chargeMaxD: 1e9,
  stalkDist: 0,
  anvilWeight: 0,
  aimLead: 0,
  // Bombardier tactics (SkirmishBrain._bomberOrders) — bombControl false
  // leaves bombardiers on the built-in AI.
  bombControl: false,
  bombDist: CONST.BOMBARDIER_PREFERRED_DIST,
  bombBehind: 0,
  bombWhenStuck: false,
  bombWhenEngaged: 0,
  bombEngagedRadius: 60,
  bombWhenSwinging: false,
  bombLead: CONST.BOMB_LEAD_FACTOR,
  bombCutoff: 0,
  // Archer tactics (SkirmishBrain._archerOrders) — archerControl false
  // leaves archers on the built-in AI.
  archerControl: false,
  arrowDist: CONST.ARCHER_PREFERRED_DIST,
  arrowBehind: 0,
  arrowFan: 0,
  arrowWhenStuck: false,
  arrowWhenEngaged: 0,
  arrowEngagedRadius: 60,
  arrowWhenSwinging: false,
  arrowWhenNoDash: false,
  arrowVolley: 1,
  arrowLead: 0.65,
  // Patience cap for the lancer / bombardier / archer timing gates: ready
  // this many seconds without the gate opening → strike anyway. 0 = wait
  // forever (a player who never swings is never bombed).
  gateMaxWait: 0,
};
export const SKIRMISH_OFF = {
  holdDist: 1e9, groupRadius: 40, readyLead: 10, retreatDist: 0, spread: 0, waitSwing: 0, bossRally: 0,
  massThreshold: 0, linkDist: 120,
  lead: 0, flankWidth: 0, flankCloseDist: 80, ringSlots: 0, anchorFrac: 0, anchorRadius: 60, useLeash: false, leash: 1500,
};

export class SkirmishBrain {
  constructor(params = {}) {
    this.params = { ...SKIRMISH_DEFAULTS, ...params };
    this._orders = new Map();
    this._waited = new WeakMap(); // guard → seconds its strike has been ready
  }

  // Has `g` been ready to strike for gateMaxWait seconds? Call once per
  // guard per tick: it also advances (or resets) the guard's wait.
  _outOfPatience(g, ready, dt) {
    const w = ready ? (this._waited.get(g) || 0) + dt : 0;
    this._waited.set(g, w);
    return this.params.gateMaxWait > 0 && w >= this.params.gateMaxWait;
  }

  plan(view) {
    const P = this.params;
    const player = view.player;
    const p = player.pos;
    this._orders.clear();
    const boss = P.bossRally > 0.5 ? view.caravans.find(c => c.isBoss) : null;
    const melee = view.guards.filter(g => !g.def.ranged && (boss || g.state !== GuardState.PATROL));
    if (boss) {
      // Ranged rally too: walk to the boss, stand and shoot once in range.
      for (const g of view.guards) {
        if (!g.def.ranged) continue;
        const inRange = g.pos.dist(p) < g.attackRange;
        this._orders.set(g, { moveTo: inRange ? g.pos : boss.pos.add(g.patrolOffset) });
      }
    }
    if (P.bombControl && !boss) this._bomberOrders(view, melee);
    if (P.archerControl && !boss) this._archerOrders(view, melee);
    if (melee.length === 0) return;

    // Hold point: the boss on a rally, otherwise the group's own centroid.
    let cx = 0;
    let cy = 0;
    for (const g of melee) { cx += g.pos.x; cy += g.pos.y; }
    const centroid = boss ? boss.pos : new Vec2(cx / melee.length, cy / melee.length);
    const holding = centroid.dist(p) > P.holdDist;

    // Critical mass: melee of a group too weak to win fall back to the
    // nearest other group — unless nobody could win anyway.
    const fallback = new Map();
    if (P.massThreshold > 0 && !holding) {
      const groups = linkGroups(view.guards, P.linkDist);
      const groupOf = new Map();
      groups.forEach(gr => gr.forEach(g => groupOf.set(g, gr)));
      if (fightRatio(view.guards, player) >= P.massThreshold) {
        const centre = gr => {
          let x = 0;
          let y = 0;
          for (const g of gr) { x += g.pos.x; y += g.pos.y; }
          return new Vec2(x / gr.length, y / gr.length);
        };
        const centres = new Map(groups.map(gr => [gr, centre(gr)]));
        for (const gr of groups) {
          if (fightRatio(gr, player) >= P.massThreshold) continue;
          let best = null;
          let bd = Infinity;
          for (const other of groups) {
            if (other === gr) continue;
            const d = centres.get(other).dist(centres.get(gr));
            if (d < bd) { bd = d; best = other; }
          }
          if (best) for (const g of gr) fallback.set(g, centres.get(best));
        }
      }
    }

    const closeIn = this._closeInPlanner(melee, player, centroid);
    const anchors = P.anchorFrac > 0 ? anchorSet(melee, P.anchorFrac) : null;

    for (const g of melee) {
      let target;
      const c = g.caravan.alive ? g.caravan : null;
      if (P.useLeash && c && g.pos.dist(c.pos) > P.leash) {
        this._orders.set(g, { moveTo: c.pos });
        continue;
      }
      if (fallback.has(g)) {
        this._orders.set(g, { moveTo: fallback.get(g) });
        continue;
      }
      if (anchors && anchors.has(g)) {
        const toPlayer = p.sub(c.pos);
        if (toPlayer.len() > P.anchorRadius + 40) {
          this._orders.set(g, { moveTo: c.pos.add(toPlayer.normalize().mul(P.anchorRadius)) });
          continue;
        }
      }
      if (holding) {
        const off = g.pos.sub(centroid);
        target = off.len() > P.groupRadius ? centroid.add(off.normalize().mul(P.groupRadius)) : g.pos;
        this._orders.set(g, { moveTo: target });
        continue;
      }
      const d = g.pos.dist(p);
      const reach = g.attackRange + player.radius;
      const ready = g.attackTimer <= P.readyLead;
      const swingOpen = P.waitSwing <= 0.5 || !player.attackReady || d < reach;
      if (ready && swingOpen) {
        target = closeIn(g, d);
      } else if (P.retreatDist > 0) {
        const away = d > 1 ? g.pos.sub(p).mul(1 / d) : new Vec2(1, 0);
        target = p.add(away.mul(Math.max(P.retreatDist, reach + 5)));
      } else {
        target = closeIn(g, d);
      }
      // Spread: step away from the nearest fellow guard inside `spread`.
      if (P.spread > 0) {
        let nearest = null;
        let nd = P.spread;
        for (const o of melee) {
          if (o === g) continue;
          const od = o.pos.dist(g.pos);
          if (od < nd) { nd = od; nearest = o; }
        }
        if (nearest) {
          const push = nd > 1e-6 ? g.pos.sub(nearest.pos).mul(1 / nd) : new Vec2(1, 0);
          target = target.add(push.mul(P.spread - nd));
        }
      }
      this._orders.set(g, g.def.charges ? this._lancerOrder(g, target, view, melee) : { moveTo: target });
    }
    // Lancers that got a hold / fall-back / anchor / leash order above still
    // obey the lunge gates.
    for (const g of melee) {
      const o = g.def.charges && this._orders.get(g);
      if (o && !('charge' in o)) o.charge = this._lancerGate(g, view, melee);
    }
  }

  // How a guard closes in on the player: straight to the nearest point by
  // default; ring slot / intercept + pincer when those are switched on and
  // the guard is still outside flankCloseDist.
  _closeInPlanner(melee, player, centroid) {
    const P = this.params;
    const p = player.pos;
    const straight = g => towardSurface(g.pos, player);
    if (P.ringSlots > 0.5) {
      const bearing = g => Math.atan2(g.pos.y - p.y, g.pos.x - p.x);
      const sorted = melee.slice().sort((a, b) => bearing(a) - bearing(b));
      const base = sorted.length ? bearing(sorted[0]) : 0;
      const slot = new Map(sorted.map((g, i) => {
        const a = base + (i / sorted.length) * Math.PI * 2;
        return [g, p.add(new Vec2(Math.cos(a), Math.sin(a)).mul(player.radius))];
      }));
      return (g, d) => (d > P.flankCloseDist ? slot.get(g) : straight(g));
    }
    if (P.lead <= 0 && P.flankWidth <= 0) return straight;
    const axis = p.sub(centroid);
    const axisDir = axis.lenSq() > 1 ? axis.normalize() : new Vec2(1, 0);
    const normal = new Vec2(-axisDir.y, axisDir.x);
    const lateral = g => g.pos.sub(p).dot(normal);
    const lats = melee.map(lateral).sort((a, b) => a - b);
    const latMedian = lats[Math.floor(lats.length / 2)];
    return (g, d) => {
      if (d <= P.flankCloseDist) return straight(g);
      const t = Math.min(PACK_MAX_LEAD_SEC, P.lead * d / g.speed);
      const side = lateral(g) < latMedian ? -1 : 1;
      const fade = Math.min(1, (d - P.flankCloseDist) / Math.max(1, P.flankCloseDist));
      return p.add(player.vel.mul(t)).add(normal.mul(side * P.flankWidth * fade));
    };
  }

  // Lancer tactics on top of the engaged order (all off by default → the
  // plain { moveTo } every melee guard gets):
  //   when to lunge — any enabled gate must hold: player glued by a sticky
  //     arrow, his dash on cooldown, ≥ N melee around him, or him swinging;
  //     and the distance within [chargeMinD, chargeMaxD] (the mechanical
  //     70–190 band still applies on top);
  //   where to wait — stalkDist from the player (also right after a lunge),
  //     rotated by anvilWeight to the side opposite the pack, so the
  //     knock-back throws him into it;
  //   where to aim — aimLead s ahead of him during the windup (chargeAim).
  // Whether a lancer may lunge now — used for every order a lancer gets
  // (holding, falling back, engaged), so the gates mean what they say.
  _lancerGate(g, view, melee) {
    const P = this.params;
    const player = view.player;
    const p = player.pos;
    const d = g.pos.dist(p);
    const gates = [];
    if (P.chargeWhenStuck) gates.push(player.stuck);
    if (P.chargeWhenNoDash) gates.push(!player.dashReady);
    if (P.chargeWhenEngaged > 0) {
      const near = melee.filter(o => o !== g && o.pos.dist(p) < P.engagedRadius).length;
      gates.push(near >= P.chargeWhenEngaged);
    }
    if (P.chargeWhenSwinging) gates.push(player.attacking);
    const impatient = this._outOfPatience(g, g.chargeCooldown <= 0 && g.chargePhase == null, view.dt);
    const gateOpen = gates.length === 0 || gates.some(Boolean) || !packNear(melee, p, g) || impatient;
    return gateOpen && d >= P.chargeMinD && d <= P.chargeMaxD;
  }

  _lancerOrder(g, target, view, melee) {
    const P = this.params;
    const player = view.player;
    const p = player.pos;
    const d = g.pos.dist(p);
    const charge = this._lancerGate(g, view, melee);
    const order = { moveTo: target, charge };
    if (P.stalkDist > 0 && !(charge && g.chargeCooldown <= 0)) {
      let dir = d > 1 ? g.pos.sub(p).mul(1 / d) : new Vec2(1, 0);
      if (P.anvilWeight > 0 && melee.length > 1) {
        let cx = 0;
        let cy = 0;
        let n = 0;
        for (const o of melee) if (o !== g) { cx += o.pos.x; cy += o.pos.y; n++; }
        const toPack = new Vec2(cx / n - p.x, cy / n - p.y);
        if (toPack.lenSq() > 1) {
          // Stand opposite the pack: the lunge then points into it.
          const mix = dir.mul(1 - P.anvilWeight).add(toPack.normalize().mul(-P.anvilWeight));
          if (mix.lenSq() > 1e-6) dir = mix.normalize();
        }
      }
      order.moveTo = p.add(dir.mul(P.stalkDist));
    }
    if (P.aimLead > 0 && g.chargePhase === 'windup') {
      order.chargeAim = p.add(player.vel.mul(P.aimLead));
    }
    return order;
  }

  // Bombardiers under the brain (bombControl): bombs never hurt guards, so
  // a lob into the player's melee scrum is free damage.
  //   where to stand — bombDist from the player, rotated by bombBehind
  //     toward the side of the melee pack (behind the guards' backs);
  //   when to throw — any enabled gate (all off without a pack near him): player glued (half speed, no dash:
  //     he can't leave the 46 px blast in the 1.25 s fuse), ≥ N melee within
  //     bombEngagedRadius of him, or him swinging; otherwise hold the bomb;
  //   where to aim — bombLead × fuse ahead of him, shifted bombCutoff px
  //     toward his escape side (away from the pack) to cut the retreat off.
  _bomberOrders(view, melee) {
    const P = this.params;
    const player = view.player;
    const p = player.pos;
    const bombers = view.guards.filter(g => g.type === 'bombardier' && g.state !== GuardState.PATROL);
    if (bombers.length === 0) return;
    const toPack = packDir(melee, p);
    const gates = [];
    if (P.bombWhenStuck) gates.push(player.stuck);
    if (P.bombWhenEngaged > 0) gates.push(melee.filter(g => g.pos.dist(p) < P.bombEngagedRadius).length >= P.bombWhenEngaged);
    if (P.bombWhenSwinging) gates.push(player.attacking);
    const throwNow = gates.length === 0 || gates.some(Boolean) || !packNear(melee, p);
    let aim = p.add(player.vel.mul(CONST.BOMB_FUSE_SEC * P.bombLead));
    if (toPack && P.bombCutoff > 0) aim = aim.add(toPack.mul(-P.bombCutoff));
    for (const g of bombers) {
      const d = g.pos.dist(p);
      let dir = d > 1 ? g.pos.sub(p).mul(1 / d) : new Vec2(1, 0);
      if (toPack && P.bombBehind > 0) {
        const mix = dir.mul(1 - P.bombBehind).add(toPack.mul(P.bombBehind));
        if (mix.lenSq() > 1e-6) dir = mix.normalize();
      }
      const impatient = this._outOfPatience(g, g.attackTimer <= 0, view.dt);
      const attack = throwNow || impatient;
      this._orders.set(g, { moveTo: p.add(dir.mul(P.bombDist)), attack, bombAim: aim });
    }
  }

  // Archers under the brain (archerControl):
  //   where to stand — arrowDist from the player, rotated by arrowBehind
  //     toward the melee pack (a screen between him and the bows), and
  //     pushed apart to arrowFan degrees around him (crossfire: a sidestep
  //     out of one line walks into another); within 0.3 × arrowDist of that
  //     post — stay put; rushed (< 0.7 × arrowDist) — back off to the caravan;
  //   when to shoot — any enabled gate (all off without a pack near him): player glued, ≥ N melee within
  //     arrowEngagedRadius of him, him swinging, his dash on cooldown (no
  //     i-frames to eat the arrow); and at least arrowVolley archers ready
  //     in range at once (a volley); otherwise hold the arrow;
  //   where to aim — arrowLead × flight time ahead of him (built-in 0.65).
  _archerOrders(view, melee) {
    const P = this.params;
    const player = view.player;
    const p = player.pos;
    const archers = view.guards.filter(g => g.type === 'archer' && g.state !== GuardState.PATROL);
    if (archers.length === 0) return;
    const toPack = packDir(melee, p);
    const gates = [];
    if (P.arrowWhenStuck) gates.push(player.stuck);
    if (P.arrowWhenEngaged > 0) gates.push(melee.filter(g => g.pos.dist(p) < P.arrowEngagedRadius).length >= P.arrowWhenEngaged);
    if (P.arrowWhenSwinging) gates.push(player.attacking);
    if (P.arrowWhenNoDash) gates.push(!player.dashReady);
    const ready = archers.filter(g => g.attackTimer <= 0 && g.pos.dist(p) < g.attackRange).length;
    const volley = ready >= Math.min(Math.round(P.arrowVolley), archers.length);
    const shoot = volley && (gates.length === 0 || gates.some(Boolean) || !packNear(melee, p));
    // Angle of each archer around the player, after the pull toward the pack.
    const angles = archers.map(g => {
      const d = g.pos.dist(p);
      let dir = d > 1 ? g.pos.sub(p).mul(1 / d) : new Vec2(1, 0);
      if (toPack && P.arrowBehind > 0) {
        const mix = dir.mul(1 - P.arrowBehind).add(toPack.mul(P.arrowBehind));
        if (mix.lenSq() > 1e-6) dir = mix.normalize();
      }
      return Math.atan2(dir.y, dir.x);
    });
    if (P.arrowFan > 0 && archers.length > 1) spreadAngles(angles, P.arrowFan * Math.PI / 180);
    archers.forEach((g, i) => {
      const d = g.pos.dist(p);
      const travel = d / CONST.PROJECTILE_SPEED;
      let moveTo = p.add(new Vec2(Math.cos(angles[i]), Math.sin(angles[i])).mul(P.arrowDist));
      if (d < P.arrowDist * 0.7) {
        // Rushed: fall back toward the own caravan like the built-in AI —
        // backing straight away from a faster player kites him across the
        // map and stalls the wave.
        const back = g.caravan.pos.sub(g.pos);
        if (back.lenSq() > 1) moveTo = g.pos.add(back.normalize().mul(P.arrowDist));
      } else if (g.pos.dist(moveTo) < P.arrowDist * 0.3) {
        moveTo = g.pos; // close enough to the post: stand and shoot
      }
      this._orders.set(g, {
        moveTo,
        attack: this._outOfPatience(g, g.attackTimer <= 0 && d < g.attackRange, view.dt) || shoot,
        arrowAim: p.add(player.vel.mul(travel * P.arrowLead)),
      });
    });
  }

  order(guard) {
    return this._orders.get(guard) || null;
  }
}

// True when some melee guard other than `self` is within 250 px of p. The
// timing gates (wait until he is engaged / swinging / glued…) only make
// sense with a pack to set the moment up; without one they would hold fire
// forever against a player who just stands still.
function packNear(melee, p, self = null) {
  return melee.some(o => o !== self && o.pos.dist(p) < 250);
}

// Unit vector from p toward the centroid of the melee within 250 px, or null.
function packDir(melee, p) {
  const pack = melee.filter(g => g.pos.dist(p) < 250);
  if (!pack.length) return null;
  let cx = 0;
  let cy = 0;
  for (const g of pack) { cx += g.pos.x; cy += g.pos.y; }
  const v = new Vec2(cx / pack.length - p.x, cy / pack.length - p.y);
  return v.lenSq() > 1 ? v.normalize() : null;
}

// Push angles (radians, in place) apart so neighbours around the circle are
// at least `gap` apart where possible — a few relaxation passes.
function spreadAngles(angles, gap) {
  const n = angles.length;
  gap = Math.min(gap, 2 * Math.PI / n);
  for (let pass = 0; pass < 4; pass++) {
    const order = angles.map((a, i) => i).sort((i, j) => angles[i] - angles[j]);
    for (let k = 0; k < n; k++) {
      const i = order[k];
      const j = order[(k + 1) % n];
      let d = angles[j] - angles[i];
      if (k === n - 1) d += 2 * Math.PI;
      if (d < gap) {
        const push = (gap - d) / 2;
        angles[i] -= push;
        angles[j] += push;
      }
    }
  }
}

// Per caravan, the engaged melee nearest to it (share `frac`) hold the line.
function anchorSet(melee, frac) {
  const byCaravan = new Map();
  for (const g of melee) {
    if (!g.caravan.alive) continue;
    if (!byCaravan.has(g.caravan)) byCaravan.set(g.caravan, []);
    byCaravan.get(g.caravan).push(g);
  }
  const out = new Set();
  for (const [c, gs] of byCaravan) {
    gs.sort((a, b) => a.pos.dist(c.pos) - b.pos.dist(c.pos));
    gs.slice(0, Math.round(frac * gs.length)).forEach(g => out.add(g));
  }
  return out;
}

// Guard tactics v4, evolved in ~/dev/corovans-ai (evo/champions.json):
// skirmish rhythm + critical mass, lancer aim lead, bombardier targeting,
// archer crossfire and shot timing. The default guard brain of the 1.7
// build (see main.js).
export const GUARD_V4 = {
  holdDist: 966.465,
  groupRadius: 57.69,
  readyLead: 0.199,
  retreatDist: 0,
  spread: 40.219,
  waitSwing: false,
  bossRally: false,
  massThreshold: 0.853,
  linkDist: 27.107,
  lead: 0.423,
  flankWidth: 15.278,
  flankCloseDist: 92.785,
  ringSlots: false,
  anchorFrac: 0,
  anchorRadius: 35.763,
  chargeWhenStuck: false,
  chargeWhenNoDash: false,
  chargeWhenEngaged: 0.731,
  engagedRadius: 42.763,
  chargeWhenSwinging: false,
  chargeMinD: 70,
  chargeMaxD: 190,
  stalkDist: 20.484,
  anvilWeight: 0.157,
  aimLead: 0.388,
  bombControl: true,
  bombDist: 120.597,
  bombBehind: 0.303,
  bombWhenStuck: false,
  bombWhenEngaged: 0.976,
  bombEngagedRadius: 82.472,
  bombWhenSwinging: true,
  bombLead: 0.15,
  bombCutoff: 19.371,
  archerControl: true,
  arrowDist: 70.316,
  arrowBehind: 0,
  arrowFan: 144.294,
  arrowWhenStuck: true,
  arrowWhenEngaged: 1.229,
  arrowEngagedRadius: 113.955,
  arrowWhenSwinging: true,
  arrowWhenNoDash: true,
  arrowVolley: 1.202,
  arrowLead: 1.108,
  gateMaxWait: 2,
};

// Name → factory(params). `builtin` is the absence of a brain.
export const GUARD_BRAINS = {
  surround: params => new SurroundBrain(params),
  relentless: () => new RelentlessBrain(),
  pack: params => new PackBrain(params),
  skirmish: params => new SkirmishBrain(params),
};
